import { Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import PropTypes from "prop-types";
import { StyledButton } from "react-common-ui-library";
import { useIntl } from "react-intl";
import {
  CONTAINED,
  H2,
  P_LARGE,
  P_LARGE_SEMI_BOLD,
  SM,
  SUBTITLE,
} from "../../../Constants/CommonConstants";
import {
  StyledAccountTypeIconBox,
  StyledChooseAccountTypeCard,
  StyledTypographyStack,
} from "../../../Styles/AccountCreation/ChooseAccountType/ChooseAccountTypeStyles";

/**
 * @description Common component which displays the account type card
 * @param {ReactNode} icon Icon to be displayed within the card
 * @param {string} header Header to be displayed for the card
 * @param {string} subHeader Sub-Header to be displayed for the card
 * @param {string} buttonText Button text to be displayed within the card
 * @param {function} buttonClickHandler Action to be performed on the click of card button
 * @param {string} buttonId Id for the card button
 * @returns {ReactNode} Renders the account type card
 */
export const AccountTypeCard = (props) => {
  const intl = useIntl();

  const { icon, header, subHeader, buttonText, buttonClickHandler, buttonId } =
    props;

  const theme = useTheme();

  const matches = useMediaQuery(theme.breakpoints.up(SM));

  return (
    <StyledChooseAccountTypeCard>
      <Stack gap={4}>
        <StyledAccountTypeIconBox>{icon}</StyledAccountTypeIconBox>
        <StyledTypographyStack gap={1}>
          <Typography
            component={H2}
            variant={matches ? SUBTITLE : P_LARGE_SEMI_BOLD}
            id={header}
            fontWeight={600}
          >
            {intl.formatMessage({ id: header })}
          </Typography>
          <Typography component={H2} variant={P_LARGE} id={subHeader}>
            {intl.formatMessage({ id: subHeader })}
          </Typography>
        </StyledTypographyStack>
        <StyledButton
          variant={CONTAINED}
          onClick={buttonClickHandler}
          id={buttonId}
        >
          {intl.formatMessage({ id: buttonText })}
        </StyledButton>
      </Stack>
    </StyledChooseAccountTypeCard>
  );
};

AccountTypeCard.propTypes = {
  /**
   * Icon to be displayed within the card
   */
  icon: PropTypes.node.isRequired,
  /**
   * Header to be displayed for the card
   */
  header: PropTypes.string.isRequired,
  /**
   * Sub-Header to be displayed for the card
   */
  subHeader: PropTypes.string.isRequired,
  /**
   * Button text to be displayed within the card
   */
  buttonText: PropTypes.string.isRequired,
  /**
   * Action to be performed on the click of card button
   */
  buttonClickHandler: PropTypes.func.isRequired,
  /**
   * Id for the card button
   */
  buttonId: PropTypes.string.isRequired,
};
